/**
 * Collection request types
 *
 * Types for managing Velociraptor collection requests and workflow states.
 */

// =============================================================================
// ENUM TYPES
// =============================================================================

/**
 * Package-build lifecycle of a collection request (backend `package_status`).
 *
 * Independent of `ReturnStatus` -- a request tracks package build/send
 * progress and evidence return progress as two separate dimensions (PRP §4).
 */
export type PackageStatus =
  | 'draft'
  | 'pending'
  | 'generating'
  | 'ready'
  | 'sent'
  | 'stalled'
  | 'ingested'
  | 'failed'
  | 'cancelled'
  | 'expired'

/**
 * Evidence-return progress of a collection request (backend `return_status`).
 *
 * Independent of `PackageStatus` (PRP §4).
 */
export type ReturnStatus = 'none' | 'partially_returned' | 'fully_returned' | 'completed'

/**
 * Exhaustive `PackageStatus` value list, mirrored from the backend
 * `ALL_PACKAGE_STATUSES` const (PRP §2 C2) -- used for type-guarding raw
 * status strings coming back from count/summary endpoints.
 */
export const ALL_PACKAGE_STATUSES: PackageStatus[] = [
  'draft',
  'pending',
  'generating',
  'ready',
  'sent',
  'stalled',
  'ingested',
  'failed',
  'cancelled',
  'expired',
]

/**
 * Exhaustive `ReturnStatus` value list, mirrored from the backend
 * `ALL_RETURN_STATUSES` const (PRP §2 C2).
 */
export const ALL_RETURN_STATUSES: ReturnStatus[] = [
  'none',
  'partially_returned',
  'fully_returned',
  'completed',
]

/**
 * Legacy single-dimension collection-request status vocabulary.
 *
 * Superseded by `PackageStatus` + `ReturnStatus` (PRP §4) -- the backend no
 * longer returns a `status` field. Retained only for the unmounted
 * collection-request-list/card/detail-panel widgets pending the Phase 2 UX
 * redesign; do not use for new code.
 */
export type CollectionRequestStatus =
  | 'draft'
  | 'pending_generation'
  | 'generating'
  | 'ready'
  | 'sent'
  | 'awaiting_return'
  | 'returned'
  | 'ingesting'
  | 'completed'
  | 'failed'
  | 'cancelled'

/**
 * Type of collector used
 */
export type CollectorType = 'velociraptor' | 'manual' | 'kape' | 'custom'

/**
 * Priority level for collection targets
 */
export type CollectionPriority = 'critical' | 'high' | 'medium' | 'low'

/**
 * Collection method for targets
 */
export type CollectionMethod = 'offline_collector' | 'live_collection' | 'manual_retrieval'

// =============================================================================
// ENTITY TYPES
// =============================================================================

/**
 * Collection request entity (API response)
 */
export interface CollectionRequest {
  id: string
  caseId: string
  requestName: string
  collectorType: string
  /** Number of endpoints (collection targets) attached to this request. */
  targetCount: number
  isLocked: boolean
  artifactSet: string | null
  selectedArtifacts: Record<string, unknown> | null
  collectionOptions: Record<string, unknown> | null
  packageHash: string | null
  packageSizeBytes: number | null
  packageGeneratedAt: string | null
  packageStatus: PackageStatus
  returnStatus: ReturnStatus
  isExternal: boolean
  stallReason: string | null
  statusUpdatedAt: string
  sentToContact: string | null
  sentAt: string | null
  expectedCompletion: string | null
  returnedAt: string | null
  ingestedAt: string | null
  ingestedArtifactCount: number | null
  notes: string | null
  statusHistory: Record<string, unknown>
  createdBy: string | null
  createdAt: string
  updatedAt: string
  artefactTemplateId: string | null
  /**
   * Resolved artefact-template configuration snapshot, materialised by the
   * backend when an artefact template is assigned to the request. The
   * backend's `POST /collection-requests/:id/generate` endpoint requires this
   * to be non-null (returns 400 otherwise). FR5 gates the "Generate" CTA on
   * this field's presence.
   */
  resolvedArtefactConfig?: Record<string, unknown> | null
}

/**
 * Status history entry
 */
export interface StatusHistoryEntry {
  status: string
  timestamp: string
  userId: string | null
  notes: string | null
}

/**
 * Collection target entity
 */
export interface CollectionTarget {
  id: string
  collectionRequestId: string
  endpointId: string
  endpointHostname: string
  endpointDeviceType: string
  endpointSystemGuid: string | null
  collectionMethod: string
  priority: string
  status: string
  statusUpdatedAt: string
  notes: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Legacy single-dimension status count.
 *
 * Superseded by `StatusCountResponse` (two-dimension filter contract, PRP
 * §4). Retained only for the unmounted dashboard/list widgets pending the
 * Phase 2 UX redesign; do not use for new code.
 */
export interface StatusCount {
  status: string
  count: number
}

/**
 * One status value's request count within a dimension.
 */
export interface DimensionStatusCount {
  status: string
  count: number
}

/**
 * Response for status counts, both dimensions (mirrors the backend
 * `StatusCountResponse` field-for-field).
 */
export interface StatusCountResponse {
  packageStatus: DimensionStatusCount[]
  returnStatus: DimensionStatusCount[]
}

// =============================================================================
// INPUT TYPES
// =============================================================================

/**
 * Input for creating a collection request
 */
export interface CreateCollectionRequestInput {
  requestName: string
  collectorType: string
  targetEndpointIds?: string[]
  artifactSet?: string
  selectedArtifacts?: Record<string, unknown>
  collectionOptions?: Record<string, unknown>
  notes?: string
}

/**
 * Input for updating a collection request
 */
export interface UpdateCollectionRequestInput {
  requestName: string
  notes?: string
  artefactTemplateId?: string
}

/**
 * Input for updating package info
 */
export interface UpdatePackageInfoInput {
  packageHash: string
  packageSizeBytes: number
}

/**
 * Input for marking request as sent
 */
export interface MarkSentInput {
  sentToContact: string
  expectedCompletion?: string
}

/**
 * Input for marking request as ingested
 */
export interface MarkIngestedInput {
  artifactCount: number
}

/**
 * Input for the manual status-override endpoint.
 *
 * `dimension` selects which status track is being corrected; `status` is
 * validated server-side against that dimension's vocabulary; `reason` is
 * mandatory and audited (PRP §4).
 */
export interface OverrideRequestStatusInput {
  dimension: 'package' | 'return'
  status: string
  reason: string
}

/**
 * Input for the stall endpoint (`sent` -> `stalled`).
 */
export interface StallRequestInput {
  reason: string
}

/**
 * Input for adding targets to a collection request
 */
export interface AddTargetsInput {
  endpointIds: string[]
}

/**
 * Result of adding targets to a collection request
 */
export interface AddTargetsResult {
  added: CollectionTarget[]
  duplicates: string[]
}

/**
 * Input for removing targets from a collection request
 */
export interface RemoveTargetsInput {
  targetIds: string[]
}

/**
 * Result of removing targets from a collection request
 */
export interface RemoveTargetsResult {
  removedCount: number
}

// =============================================================================
// QUERY PARAMS
// =============================================================================

/**
 * Query parameters for listing collection requests
 *
 * `status` was replaced by the two-dimension filter contract (PRP §4).
 */
export interface ListCollectionRequestsParams {
  packageStatus?: PackageStatus
  returnStatus?: ReturnStatus
  isExternal?: boolean
  collectorType?: string
}

// =============================================================================
// RESPONSE TYPES
// =============================================================================

/**
 * Document response (markdown/form)
 */
export interface DocumentResponse {
  content: string
  contentType: string
  filename: string
}

// =============================================================================
// DISPLAY CONSTANTS
// =============================================================================

/**
 * Labels for collection request status
 */
export const COLLECTION_REQUEST_STATUS_LABELS: Record<CollectionRequestStatus, string> = {
  draft: 'Draft',
  pending_generation: 'Pending Generation',
  generating: 'Generating',
  ready: 'Ready',
  sent: 'Sent',
  awaiting_return: 'Awaiting Return',
  returned: 'Returned',
  ingesting: 'Ingesting',
  completed: 'Completed',
  failed: 'Failed',
  cancelled: 'Cancelled',
}

/**
 * Labels for collector types
 */
export const COLLECTOR_TYPE_LABELS: Record<CollectorType, string> = {
  velociraptor: 'Velociraptor',
  manual: 'Manual Collection',
  kape: 'KAPE',
  custom: 'Custom',
}

/**
 * Labels for collection priority
 */
export const COLLECTION_PRIORITY_LABELS: Record<CollectionPriority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
}

/**
 * Labels for collection method
 */
export const COLLECTION_METHOD_LABELS: Record<CollectionMethod, string> = {
  offline_collector: 'Offline Collector',
  live_collection: 'Live Collection',
  manual_retrieval: 'Manual Retrieval',
}

/**
 * Colours for status badges
 */
export const COLLECTION_REQUEST_STATUS_COLOURS: Record<CollectionRequestStatus, string> = {
  draft: 'gray',
  pending_generation: 'yellow',
  generating: 'blue',
  ready: 'green',
  sent: 'purple',
  awaiting_return: 'orange',
  returned: 'teal',
  ingesting: 'blue',
  completed: 'green',
  failed: 'red',
  cancelled: 'gray',
}
